import React from 'react';
import { Sparkles, Heart, Star, Leaf, Award, Home } from 'lucide-react';

const AboutPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-purple-50 to-pink-50">
      {/* Bouton retour */}
      <div className="container mx-auto px-4 pt-8">
        <button
          onClick={onBack}
          className="group flex items-center gap-2 text-purple-700 hover:text-purple-900 transition-colors"
        >
          <Home className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Retour à l'accueil</span>
        </button>
      </div>

      {/* Hero Section */}
      <section className="container mx-auto px-4 py-16">
        <div className="max-w-4xl mx-auto text-center">
          {/* Titre principal avec ornements */}
          <div className="relative mb-12">
            <div className="absolute -top-8 left-1/2 -translate-x-1/2">
              <Sparkles className="w-12 h-12 text-amber-400 animate-pulse" />
            </div>
            
            <h1 className="text-4xl md:text-5xl font-light text-purple-900 mb-4 leading-tight">
              Bienvenue dans l'univers d'
              <span className="block text-5xl md:text-6xl font-medium bg-gradient-to-r from-amber-600 via-purple-600 to-pink-600 bg-clip-text text-transparent mt-2">
                Alaia Cristal Bien-être
              </span>
            </h1>

            <div className="flex items-center justify-center gap-4 mt-8">
              <div className="h-px w-24 bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
              <Star className="w-6 h-6 text-amber-500" />
              <div className="h-px w-24 bg-gradient-to-r from-transparent via-amber-400 to-transparent"></div>
            </div>
          </div>

          {/* Citation inspirante */}
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-8 shadow-xl border border-amber-200/50 mb-16">
            <p className="text-xl md:text-2xl text-gray-700 font-light italic leading-relaxed">
              "Un moment hors du temps, une parenthèse sacrée dédiée à votre bien-être"
            </p>
          </div>
        </div>
      </section>

      {/* Section valeurs */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            {/* Douceur */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-amber-100">
              <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Heart className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-medium text-purple-900 mb-3 text-center">Douceur</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Une approche délicate et enveloppante pour un bien-être profond
              </p>
            </div>

            {/* Équilibre */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-purple-100">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Leaf className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-medium text-purple-900 mb-3 text-center">Équilibre</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Retrouver l'harmonie entre le corps et l'esprit
              </p>
            </div>

            {/* Sérénité */}
            <div className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-shadow border border-pink-100">
              <div className="w-16 h-16 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center mb-6 mx-auto">
                <Star className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-2xl font-medium text-purple-900 mb-3 text-center">Sérénité</h3>
              <p className="text-gray-600 text-center leading-relaxed">
                Un havre de paix pour se reconnecter à soi
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section philosophie */}
      <section className="container mx-auto px-4 py-12">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-white to-amber-50 rounded-3xl p-8 md:p-12 shadow-2xl border border-amber-200">
            <div className="flex items-center gap-3 mb-8">
              <div className="w-1 h-12 bg-gradient-to-b from-amber-400 to-purple-600 rounded-full"></div>
              <h2 className="text-3xl font-light text-purple-900">Notre Philosophie</h2>
            </div>

            <div className="space-y-6 text-gray-700 leading-relaxed">
              <p className="text-lg">
                <span className="font-medium text-purple-900">Alaia Cristal Bien-être</span> incarne la douceur, 
                l'équilibre et la sérénité, invitant à ralentir, se reconnecter à soi et retrouver 
                l'harmonie et la paix intérieure.
              </p>

              <p className="text-lg">
                Chaque soin est pensé comme un <span className="font-medium text-amber-700">voyage sensoriel</span> pour 
                un rituel personnalisé et une parenthèse sacrée. C'est un moment hors du temps, 
                associé à une écoute attentive et une gestuelle professionnelle.
              </p>
            </div>

            {/* Ornement décoratif */}
            <div className="flex justify-center my-8">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 rounded-full bg-amber-400"></div>
                <div className="w-3 h-3 rounded-full bg-purple-400"></div>
                <div className="w-2 h-2 rounded-full bg-pink-400"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section Virginie */}
      <section className="container mx-auto px-4 py-12 mb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-purple-900 to-purple-700 rounded-3xl p-8 md:p-12 shadow-2xl text-white relative overflow-hidden">
            {/* Motifs décoratifs */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>

            <div className="relative z-10">
              {/* Badge expérience */}
              <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 mb-8 border border-white/20">
                <Award className="w-6 h-6 text-amber-300" />
                <span className="font-medium text-amber-100">36 ans d'expérience</span>
              </div>

              <h2 className="text-3xl md:text-4xl font-light mb-6">
                Virginie, votre praticienne bien-être
              </h2>

              <div className="space-y-6 text-purple-100 leading-relaxed">
                <p className="text-lg">
                  Virginie a consacré sa carrière au bien-être, à la beauté et à l'harmonie du corps et de l'esprit. 
                  Praticienne bien-être, elle bénéficie de <span className="font-bold text-amber-300">36 ans d'expérience</span> dans 
                  le domaine de la beauté, complétée par plusieurs années au sein de l'univers du spa.
                </p>

                <p className="text-lg">
                  Elle a développé une <span className="font-medium text-white">approche du soin global et intuitive</span> en 
                  se spécialisant dans les massages du visage et du corps où chaque geste devient un 
                  langage subtil de relaxation.
                </p>

                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/20 mt-8">
                  <p className="text-lg text-white italic">
                    "Virginie vous accompagne avec bienveillance vers un profond lâcher-prise sur un chemin 
                    de douceur, de paix et de beauté consciente au sein du Spa Eclipsea à Bessines, dans un 
                    lieu propice au lâcher-prise et à la reconnexion à soi."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section CTA finale */}
      <section className="container mx-auto px-4 pb-20">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-to-r from-amber-100 via-purple-100 to-pink-100 rounded-3xl p-12 shadow-xl">
            <Sparkles className="w-16 h-16 text-amber-500 mx-auto mb-6" />
            <h3 className="text-3xl font-light text-purple-900 mb-4">
              Prêt à vivre cette expérience ?
            </h3>
            <p className="text-gray-700 mb-8 text-lg">
              Offrez-vous un moment de détente absolue et de reconnexion à soi
            </p>
            <button
              onClick={onBack}
              className="bg-gradient-to-r from-amber-500 via-purple-600 to-pink-600 text-white px-10 py-4 rounded-full text-lg font-medium shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105"
            >
              Découvrir nos soins
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;